import React, { useState } from "react";

const PaymentMethodModal = ({
  isOpen,
  onClose,
  onSelectPaymentMethod,
  amount = "₹4,563",
}) => {
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [selectedBank, setSelectedBank] = useState("");

  if (!isOpen) return null;

  const toggleMethod = (method) => {
    setExpandedMethod((prev) => (prev === method ? null : method));
  };

  const handleSelect = (method) => {
    onSelectPaymentMethod?.(method);
  };

  const banks = [
    "Emirates NBD",
    "First Abu Dhabi Bank (FAB)",
    "Abu Dhabi Commercial Bank (ADCB)",
    "Mashreq Bank",
    "Dubai Islamic Bank (DIB)",
    "Emirates Islamic",
    "RAKBANK",
    "Commercial Bank of Dubai (CBD)",
    "Ajman Bank",
    "Sharjah Islamic Bank",
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#26351d]/45 px-3 py-4 backdrop-blur-md sm:px-5"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[94vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[24px] border border-white/80 bg-white shadow-[0_30px_100px_rgba(55,92,32,0.28)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-[#edf0e9] bg-white px-5 pb-4 pt-5 sm:px-7 sm:pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              {/* Secure Badge */}
              <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-[#dce8d5] bg-[#f2f7ee] px-3 py-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 text-[#477132]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect
                    x="5"
                    y="10"
                    width="14"
                    height="10"
                    rx="2"
                  />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                </svg>

                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#4d7138]">
                  Secure Payment
                </span>
              </div>

              <h2 className="text-[21px] font-bold tracking-[-0.025em] text-[#1f281a] sm:text-[24px]">
                Choose Payment Method
              </h2>

              <p className="mt-1.5 text-[12px] leading-5 text-[#747c70] sm:text-[13px]">
                Select how you would like to fund your property investment.
              </p>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f3f5f0] text-[21px] font-light text-[#697163] transition hover:bg-[#e9eee4] hover:text-[#34412f]"
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-5 py-4 sm:px-7 sm:py-5">
          <div className="space-y-3">

            {/* =====================================================
                DEBIT CARD
            ====================================================== */}
            <div
              className={`overflow-hidden rounded-[17px] border transition-all duration-200 ${
                expandedMethod === "debit_card"
                  ? "border-[#b9cda9] bg-[#fbfcf9] shadow-[0_8px_25px_rgba(55,92,32,0.07)]"
                  : "border-[#e2e6df] bg-white"
              }`}
            >
              {/* Header */}
              <button
                type="button"
                onClick={() => toggleMethod("debit_card")}
                className="flex w-full items-center gap-3.5 p-4 text-left"
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] ${
                    expandedMethod === "debit_card"
                      ? "bg-[#e7f0e1] text-[#477132]"
                      : "bg-[#f4f7f0] text-[#63705c]"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 10h18" />
                    <path d="M7 15h3" />
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[14px] font-bold text-[#293122]">
                      Debit Card
                    </p>
                    <span className="text-[#7a8176]">(Not Available)</span>
                  </div>

                  <p className="mt-0.5 text-[11px] text-[#7a8176]">
                    Pay securely using your debit card
                  </p>
                </div>

                {/* Card Brands */}
                <div className="hidden items-center gap-1.5 sm:flex">
                  <span className="rounded bg-white px-1.5 py-1 text-[9px] font-bold italic text-[#3156a3] shadow-sm">
                    VISA
                  </span>

                  <span className="rounded bg-white px-1.5 py-1 text-[9px] font-bold text-[#bd342d] shadow-sm">
                    ●●
                  </span>

                  <span className="rounded bg-white px-1.5 py-1 text-[8px] font-bold italic text-[#7d8490] shadow-sm">
                    RuPay
                  </span>
                </div>

                <Chevron expanded={expandedMethod === "debit_card"} />
              </button>

              {/* Debit Card Form */}
              {expandedMethod === "debit_card" && (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <div className="rounded-[15px] border border-[#e5e9e2] bg-white p-4 sm:p-5">

                    <DisabledInput
                      label="Card Number"
                      placeholder="XXXX XXXX XXXX XXXX"
                      icon="card"
                    />

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="sm:col-span-1">
                        <DisabledInput
                          label="Cardholder Name"
                          placeholder="Name on card"
                        />
                      </div>

                      <DisabledInput
                        label="Valid Thru"
                        placeholder="MM / YY"
                      />

                      <DisabledInput
                        label="CVV"
                        placeholder="CVV"
                        icon="info"
                      />
                    </div>

                    <button
                      type="button"
                      disabled
                      className="mt-4 h-[46px] w-full rounded-[13px] bg-[#dfe4df] text-[13px] font-bold text-[#919891] cursor-not-allowed"
                    >
                      Pay {amount}
                    </button>

                    <p className="mt-3 text-center text-[10px] leading-4 text-[#92988e]">
                      Card payments will be available once payment
                      processing is enabled.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* =====================================================
                CREDIT CARD
            ====================================================== */}
            <div
              className={`overflow-hidden rounded-[17px] border transition-all duration-200 ${
                expandedMethod === "credit_card"
                  ? "border-[#b9cda9] bg-[#fbfcf9] shadow-[0_8px_25px_rgba(55,92,32,0.07)]"
                  : "border-[#e2e6df] bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleMethod("credit_card")}
                className="flex w-full items-center gap-3.5 p-4 text-left"
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] ${
                    expandedMethod === "credit_card"
                      ? "bg-[#e7f0e1] text-[#477132]"
                      : "bg-[#f4f7f0] text-[#63705c]"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 10h18" />
                    <path d="M7 15h3" />
                    <path d="M13 15h4" />
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-[#293122]">
                    Credit Card 
                    <span className="text-[#7a8176] font-normal"> (Not Available)</span>
                  </p>
                  

                  <p className="mt-0.5 text-[11px] text-[#7a8176]">
                    Pay securely using your credit card
                  </p>
                </div>

                <div className="hidden items-center gap-1.5 sm:flex">
                  <span className="rounded bg-white px-1.5 py-1 text-[9px] font-bold italic text-[#3156a3] shadow-sm">
                    VISA
                  </span>

                  <span className="rounded bg-white px-1.5 py-1 text-[9px] font-bold text-[#bd342d] shadow-sm">
                    ●●
                  </span>

                  <span className="rounded bg-white px-1.5 py-1 text-[8px] font-bold italic text-[#7d8490] shadow-sm">
                    RuPay
                  </span>
                </div>

                <Chevron expanded={expandedMethod === "credit_card"} />
              </button>

              {expandedMethod === "credit_card" && (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <div className="rounded-[15px] border border-[#e5e9e2] bg-white p-4 sm:p-5">
                    <DisabledInput
                      label="Card Number"
                      placeholder="XXXX XXXX XXXX XXXX"
                      icon="card"
                    />

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <DisabledInput
                        label="Cardholder Name"
                        placeholder="Name on card"
                      />

                      <DisabledInput
                        label="Valid Thru"
                        placeholder="MM / YY"
                      />

                      <DisabledInput
                        label="CVV"
                        placeholder="CVV"
                        icon="info"
                      />
                    </div>

                    <button
                      type="button"
                      disabled
                      className="mt-4 h-[46px] w-full cursor-not-allowed rounded-[13px] bg-[#dfe4df] text-[13px] font-bold text-[#919891]"
                    >
                      Pay {amount}
                    </button>

                    <p className="mt-3 text-center text-[10px] leading-4 text-[#92988e]">
                      Card payments will be available once payment
                      processing is enabled.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* =====================================================
                BANK TRANSFER
            ====================================================== */}
            <div
              className={`overflow-hidden rounded-[17px] border transition-all duration-200 ${
                expandedMethod === "bank_transfer"
                  ? "border-[#b9cda9] bg-[#fbfcf9] shadow-[0_8px_25px_rgba(55,92,32,0.07)]"
                  : "border-[#e2e6df] bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleMethod("bank_transfer")}
                className="flex w-full items-center gap-3.5 p-4 text-left"
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] ${
                    expandedMethod === "bank_transfer"
                      ? "bg-[#e7f0e1] text-[#477132]"
                      : "bg-[#f4f7f0] text-[#63705c]"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  >
                    <path d="M3 10h18" />
                    <path d="M5 10v8" />
                    <path d="M9 10v8" />
                    <path d="M15 10v8" />
                    <path d="M19 10v8" />
                    <path d="M3 18h18" />
                    <path d="M12 4l9 5H3l9-5Z" />
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-[#293122]">
                    Bank Transfer 
                    <span className="text-[#7a8176] font-normal"> (Not Available)</span>
                  </p>

                  <p className="mt-0.5 text-[11px] text-[#7a8176]">
                    Transfer funds directly from your bank account
                  </p>
                </div>

                <Chevron expanded={expandedMethod === "bank_transfer"} />
              </button>

              {expandedMethod === "bank_transfer" && (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <div className="rounded-[15px] border border-[#e5e9e2] bg-white p-4 sm:p-5">

                    <label className="mb-2 block text-[11px] font-semibold text-[#40483c]">
                      Select Your Bank
                    </label>

                    <div className="relative">
                      <select
                        value={selectedBank}
                        onChange={(e) => setSelectedBank(e.target.value)}
                        disabled
                        className="h-[48px] w-full appearance-none rounded-[12px] border border-[#d8ddd6] bg-[#fafbf9] px-4 pr-10 text-[13px] text-[#9a9f97] outline-none cursor-not-allowed"
                      >
                        <option value="">Choose your bank</option>

                        {banks.map((bank) => (
                          <option key={bank} value={bank}>
                            {bank}
                          </option>
                        ))}
                      </select>

                      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#9ba198]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </div>
                    </div>

                    {/* Bank Security Information */}
                    <div className="mt-3 flex items-start gap-2.5 rounded-[11px] bg-[#eef4fa] px-3.5 py-3">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#dceaf8] text-[#4778aa]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 11v5" />
                          <path d="M12 8h.01" />
                        </svg>
                      </div>

                      <p className="text-[10px] leading-4 text-[#667586]">
                        You will be redirected to your bank's secure page to
                        complete the transfer.
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled
                      className="mt-4 h-[46px] w-full cursor-not-allowed rounded-[13px] bg-[#dfe4df] text-[13px] font-bold text-[#919891]"
                    >
                      Continue to Bank Transfer
                    </button>

                    <p className="mt-3 text-center text-[10px] leading-4 text-[#92988e]">
                      Bank transfer payments will be enabled once the payment
                      integration is configured.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* =====================================================
                PHANTOM WALLET
            ====================================================== */}
            <div
              className={`overflow-hidden rounded-[17px] border transition-all duration-200 ${
                expandedMethod === "phantom"
                  ? "border-[#b9cda9] bg-[#fbfcf9] shadow-[0_8px_25px_rgba(55,92,32,0.07)]"
                  : "border-[#e2e6df] bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleMethod("phantom")}
                className="flex w-full items-center gap-3.5 p-4 text-left"
              >
                {/* Phantom Icon */}
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#eeeaff]">
                  <span className="text-[20px]">👻</span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[14px] font-bold text-[#293122]">
                      Phantom Wallet
                    </p>

                    <span className="rounded-full bg-[#e7f1df] px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.06em] text-[#4b7135]">
                      Recommended
                    </span>
                  </div>

                  <p className="mt-0.5 text-[11px] text-[#7a8176]">
                    Pay securely using your Solana wallet
                  </p>
                </div>

                <Chevron expanded={expandedMethod === "phantom"} />
              </button>

              {expandedMethod === "phantom" && (
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <div className="rounded-[15px] border border-[#e5e9e2] bg-white p-4 sm:p-5">
                    <div className="flex items-start gap-3 rounded-[12px] bg-[#f5f7f2] p-3.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e7f0e1]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-[#477132]"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 3v18" />
                          <path d="M5 8h14" />
                          <path d="M7 8v9" />
                          <path d="M17 8v9" />
                        </svg>
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold text-[#46503e]">
                          Solana Wallet Payment
                        </p>

                        <p className="mt-1 text-[10px] leading-4 text-[#7b8175]">
                          Connect your Phantom wallet to review and approve
                          the investment transaction securely.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleSelect({ id: "phantom" })}
                      className="mt-4 h-[46px] w-full rounded-[13px] bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-[13px] font-bold text-white shadow-[0_8px_20px_rgba(55,92,32,0.18)] transition hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(55,92,32,0.24)]"
                    >
                      Continue with Phantom
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Security Note */}
          <div className="mt-4 flex items-start gap-2.5 rounded-[13px] border border-[#e6e9e0] bg-[#f8f9f5] px-3.5 py-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e8f0e3] text-[#52743b]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 3 5 6v5c0 4.5 2.9 8.4 7 10 4.1-1.6 7-5.5 7-10V6l-7-3Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>

            <p className="text-[10px] leading-4 text-[#777e73]">
              Your payment information remains secure. Payment methods are
              currently displayed for preview purposes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-[#edf0e9] bg-[#fafbf8] px-5 py-3.5 sm:px-7">
          <button
            type="button"
            onClick={onClose}
            className="h-[42px] w-full rounded-[11px] border border-[#dce1d8] bg-white text-[12px] font-semibold text-[#596458] transition hover:bg-[#f4f6f1]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   Chevron
========================================================= */

const Chevron = ({ expanded }) => {
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6f776c] transition-transform duration-200 ${
        expanded ? "bg-[#edf3e9]" : ""
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`h-4 w-4 transition-transform duration-200 ${
          expanded ? "rotate-180" : ""
        }`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m6 9 6 6 6-6"
        />
      </svg>
    </div>
  );
};

/* =========================================================
   Disabled Input
========================================================= */

const DisabledInput = ({
  label,
  placeholder,
  icon,
}) => {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium text-[#444b40]">
        {label}
      </label>

      <div className="relative">
        <input
          type="text"
          value=""
          placeholder={placeholder}
          disabled
          readOnly
          className="h-[46px] w-full cursor-not-allowed rounded-[11px] border border-[#d8ddd6] bg-[#fafbf9] px-3.5 text-[12px] text-[#a0a59e] outline-none placeholder:text-[#a1a69f]"
        />

        {icon === "card" && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#b5bab3]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 10h18" />
            </svg>
          </div>
        )}

        {icon === "info" && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9ba198]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <path d="M12 8h.01" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodModal;